import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../api/axiosInstance';
import { SurveyOption } from '../models/types';

/**
 * The poll shown on an ANKETA post — the web counterpart of the mobile `Survey` component.
 *
 * <p>It speaks only to what the database already holds: t_survey_option (id, option_text,
 * vote_count, post_id). There is no per-user ballot, so which option you picked is remembered in
 * localStorage, exactly as mobile keeps it in AsyncStorage. That is a convenience, not a guarantee:
 * clearing site data lets the same person vote again, and the memory does not follow the account to
 * another device. Closing that hole needs a ballot table, which is a schema change, not a UI one.
 *
 * <p>Only percentages are shown, never raw counts — the same thing the app displays.
 */

interface SurveyProps {
    postId: number;
    /**
     * Options already carried by the post payload. Rendering them immediately avoids a flash of
     * empty poll while the authoritative counts are fetched.
     */
    options?: SurveyOption[];
}

const Survey: React.FC<SurveyProps> = ({ postId, options: seedOptions }) => {
    const storageKey = `survey_${postId}_selectedOption`;

    const [options, setOptions] = useState<SurveyOption[]>(seedOptions ?? []);
    const [selectedOptionId, setSelectedOptionId] = useState<number | null>(() => {
        const stored = localStorage.getItem(`survey_${postId}_selectedOption`);
        return stored ? Number(stored) : null;
    });
    const [error, setError] = useState<string | null>(null);
    /** Guards against a second vote being sent while the first round trip is still open. */
    const voting = useRef(false);

    const fetchSurveyOptions = useCallback(async () => {
        try {
            const response = await api.get(`/posts/${postId}/survey-options`);
            setOptions(response.data);
            setError(null);
        } catch (err) {
            // Deliberately visible. The mobile version only logs here, which is why a poll that
            // failed to load looked like a rendering bug rather than a request that was refused.
            setError('Anketu sa nepodarilo načítať.');
        }
    }, [postId]);

    useEffect(() => {
        fetchSurveyOptions();
    }, [fetchSurveyOptions]);

    const totalVotes = options.reduce((sum, option) => sum + option.voteCount, 0);

    /** Two decimals, matching the app, so the same poll reads identically on both. */
    const percentage = (voteCount: number) =>
        totalVotes === 0 ? '0.00' : ((voteCount / totalVotes) * 100).toFixed(2);

    const handleOptionClick = async (optionId: number) => {
        if (selectedOptionId === optionId || voting.current) {
            return;
        }
        voting.current = true;
        try {
            // Move the vote rather than adding a second one: the previous choice is decremented
            // first, so switching answers never inflates the total.
            if (selectedOptionId !== null) {
                await api.post(`/posts/${postId}/survey-options/${selectedOptionId}/decrement-vote`);
            }
            await api.post(`/posts/${postId}/survey-options/${optionId}/increment-vote`);

            localStorage.setItem(storageKey, String(optionId));
            setSelectedOptionId(optionId);
            await fetchSurveyOptions();
        } catch (err) {
            setError('Hlas sa nepodarilo uložiť.');
        } finally {
            voting.current = false;
        }
    };

    // Highest first, the same ordering the app uses, so the two clients read alike.
    const sortedOptions = [...options].sort((a, b) => b.voteCount - a.voteCount);

    if (error && options.length === 0) {
        return (
            <SurveyContainer>
                <SurveyTitle>Prieskum</SurveyTitle>
                <ErrorText>{error}</ErrorText>
            </SurveyContainer>
        );
    }

    return (
        <SurveyContainer>
            <SurveyTitle>Prieskum</SurveyTitle>
            {sortedOptions.map((option) => (
                <OptionButton
                    key={option.id}
                    type="button"
                    $selected={selectedOptionId === option.id}
                    onClick={() => handleOptionClick(option.id)}
                >
                    <OptionBar style={{ width: `${percentage(option.voteCount)}%` }} />
                    <OptionContent>
                        <OptionText>{option.optionText}</OptionText>
                        <PercentText>({percentage(option.voteCount)}%)</PercentText>
                    </OptionContent>
                </OptionButton>
            ))}
            {error && <ErrorText>{error}</ErrorText>}
        </SurveyContainer>
    );
};

const SurveyContainer = styled.div`
    margin-top: 10px;
    padding: 16px;
    background-color: #f7f7f7;
    border-radius: 10px;
`;

const SurveyTitle = styled.h3`
    font-size: 1.1em;
    margin: 0 0 14px;
    text-align: left;
    color: #333;
`;

const OptionButton = styled.button<{ $selected: boolean }>`
    position: relative;
    display: block;
    width: 100%;
    margin-bottom: 8px;
    padding: 0;
    overflow: hidden;
    border-radius: 8px;
    border: 2px solid ${({ $selected }) => ($selected ? '#f56607' : 'transparent')};
    background-color: #fff;
    cursor: pointer;
    text-align: left;
    font: inherit;

    &:hover {
        border-color: #f56607;
    }
`;

/** The filled portion is the result itself, so it sits behind the label, never over it. */
const OptionBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: rgba(245, 102, 7, 0.25);
    transition: width 0.3s ease;
`;

const OptionContent = styled.div`
    position: relative;
    z-index: 1;
    padding: 12px;
`;

const OptionText = styled.div`
    font-size: 0.95em;
    font-weight: 600;
    color: #333;
`;

const PercentText = styled.div`
    font-size: 0.8em;
    color: #555;
    margin-top: 4px;
`;

const ErrorText = styled.div`
    font-size: 0.85em;
    color: #b00020;
`;

export default Survey;
